import type { FC } from "react";
import React from "react";
import type { Key } from "react-stately";
import { Item } from "react-stately";

import type { MenuItemType } from "~/features/headerV2/HeaderV2";
import type { PageLink } from "~/root";
import { DropdownTrigger, useUiKitComponents } from "~/shared/ui-kit";
import { Flex } from "~/shared/ui-kit/flex/Flex";

type NavbarItemProps = MenuItemType & {
  currentNavlink: string;
  onNavlinkClick: (url: string, id: string) => void;
  clickHeaderSub: (targetUrl: string) => void;
  buildLink: (page: PageLink) => string;
};

function isValidMenuItem<T extends object>(
  item: T | null | undefined,
): item is T {
  return item !== null && item !== undefined;
}

export const NavbarItem: FC<NavbarItemProps> = ({
  id,
  variant,
  link,
  translations,
  items,
  currentNavlink,
  onNavlinkClick,
  clickHeaderSub,
  buildLink,
}) => {
  const { Link } = useUiKitComponents();
  const pageLink = buildLink(link);
  const translation = translations?.[0];
  const isActive = currentNavlink === id;

  const onClickDropdownItem = (key: Key): void => {
    const menuItem = items?.find(
      (item) => item?.menu_item_subItem_id?.id === key,
    );
    clickHeaderSub(buildLink(menuItem?.menu_item_subItem_id?.link));
  };

  return (
    <div className="navbar-item">
      {variant === "default" && (
        <div className={`navbar-item-content ${isActive && "active"}`}>
          <DropdownTrigger
            onAction={(key) => onClickDropdownItem(key)}
            onOpenChange={() => onNavlinkClick(pageLink, id)}
            strategy={"hover"}
            offset={40}
            triggerElement={
              <Link
                href={pageLink}
                className={`navbar-item-title base-body2-text`}
              >
                <Flex align="center" gap={10}>
                  {translation?.name}
                  <svg
                    width="24"
                    className="navbar-item-icon"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.79289 9.04289C7.18342 8.65237 7.81658 8.65237 8.20711 9.04289L12 12.8358L15.7929 9.04289C16.1834 8.65237 16.8166 8.65237 17.2071 9.04289C17.5976 9.43342 17.5976 10.0666 17.2071 10.4571L12.7071 14.9571C12.3166 15.3476 11.6834 15.3476 11.2929 14.9571L6.79289 10.4571C6.40237 10.0666 6.40237 9.43342 6.79289 9.04289Z"
                      fill="white"
                      fillOpacity="0.5"
                    />
                  </svg>
                </Flex>
              </Link>
            }
            items={items?.filter(isValidMenuItem) ?? undefined}
          >
            {(item) => (
              <Item
                key={item?.menu_item_subItem_id?.id}
                href={buildLink(item?.menu_item_subItem_id?.link)}
                textValue={
                  item?.menu_item_subItem_id?.translations?.[0]?.name ?? ""
                }
              >
                {item?.menu_item_subItem_id?.translations?.[0]?.name}
              </Item>
            )}
          </DropdownTrigger>
        </div>
      )}

      {variant === "underline" && (
        <div className="navbar-item-jackpass">
          <Link
            href={pageLink}
            onClick={() => onNavlinkClick(pageLink, id)}
            className="navbar-item-content-jackpass"
          >
            {translation?.name}
          </Link>
        </div>
      )}

      {variant === "emphasized" && (
        <div className="navbar-item-vip">
          <div className="navbar-item-vip-icon" />
          <Link
            href={pageLink}
            onClick={() => onNavlinkClick(pageLink, id)}
            className="navbar-item-content-vip"
          >
            {translation?.name}
          </Link>
          <div className="navbar-item-vip-icon" />
        </div>
      )}

      {variant === "highlighted" && (
        <Link
          className="navbar-item-leaderboard"
          href={pageLink}
          onClick={() => onNavlinkClick(pageLink, id)}
        >
          {translation?.name}
        </Link>
      )}
    </div>
  );
};
